import { apiLibrary } from "@/api";
import type { AxiosError } from "axios";
import * as Sentry from "@sentry/vue";
import { useCheckoutCartStore } from "@/stores/checkout/checkoutCartStore.ts";
import {
    useCheckoutCartLoadingStore
} from "@/stores/checkout/checkoutCartLoadingStore.ts";
import {
    useCheckoutSubmitLoadingStore
} from "@/stores/checkout/checkoutSubmittingLoadingStore.ts";
import {
    extractAttachmentsTooLargeError
} from "@/stores/checkout/composable/extractAttachmentsTooLargeError.ts";

export const useCheckoutCartManagement = () => {
    const cartStore = useCheckoutCartStore();
    const cartLoadingStore = useCheckoutCartLoadingStore();
    const submitLoadingStore = useCheckoutSubmitLoadingStore();

    const getOrCreateCart = async (): Promise<void> => {
        await cartLoadingStore.setLoadingState();

        try {
            const { data } = await apiLibrary.autoQ.checkout.getOrCreateCart();
            cartStore.setCart(data);
        } catch (err) {
            const error = err as AxiosError;
            console.error("Failed to get or create cart:", error.message);
            Sentry.captureException(error);
            throw error;
        } finally {
            await cartLoadingStore.resetLoadingState();
        }
    };

    const getCart = async (): Promise<void> => {
        await cartLoadingStore.setLoadingState();

        try {
            const { data } = await apiLibrary.autoQ.checkout.getCart();
            cartStore.setCart(data);
        } catch (err) {
            const error = err as AxiosError;
            console.error("Failed to get cart:", error.message);
            Sentry.captureException(error);
            throw error;
        } finally {
            await cartLoadingStore.resetLoadingState();
        }
    };

    const deleteCart = async (): Promise<void> => {
        await cartLoadingStore.setLoadingState();

        try {
            await apiLibrary.autoQ.checkout.deleteCart();
        } catch (err) {
            const error = err as AxiosError;
            console.error("Failed to delete cart:", error.message);
            Sentry.captureException(error);
            throw error;
        } finally {
            await cartLoadingStore.resetLoadingState();
        }
    };

    const submitCart = async () => {
        await submitLoadingStore.setLoadingState();
        try {
            await apiLibrary.autoQ.checkout.submitCart();
        } catch (err) {
            const error = err as AxiosError;

            // Too large documents are a user input problem shown in the cart, not an error to report
            if (extractAttachmentsTooLargeError(error) === null) {
                console.error(error.message);
                Sentry.captureException(error);
            }

            throw error;
        } finally {
            await submitLoadingStore.resetLoadingState();
        }

    };

    return { submitCart, getOrCreateCart, getCart, deleteCart };
};